import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useHashScroll } from '../lib/useHashScroll';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { CinematicReel } from '../components/CinematicReel';
import { CinematicBand, useReveal } from '../components/CinematicBits';
import { CapabilityExplorer, GlanceFinder, PillarStage, RoadmapTrack, ValuesAccordion, WorkflowExplorer } from '../components/AboutInteractive';
import '../styles/landing.css';
import '../styles/public.css';
import '../styles/about-interactive.css';

/*
 * The product tour — ten real screens, not drawings of them.
 *
 * This replaced three hand-built mockups. The mockups were honest and they
 * matched the theme, but they were still a description of the product written
 * by the people making the claims. Somebody deciding whether software fits
 * their week is looking for evidence, and a screenshot is evidence in a way
 * that an illustration of a screenshot is not.
 *
 * Captured from the demo account, so the figures are seeded rather than any
 * real landlord's. Sources live in PMS PROJECT DOCUMENTATION/screens; only
 * these ten were resized into web/public/screens as WebP — 1440px wide, about
 * 42KB each, because ten full-size PNGs on one page is 3MB nobody asked for.
 */
/*
 * The product tour — nine real screens, not drawings of them.
 *
 * This replaced three hand-built mockups. The mockups were honest and they
 * matched the theme, but they were still a description of the product written
 * by the people making the claims. Somebody deciding whether software fits
 * their week is looking for evidence, and a screenshot is evidence in a way
 * that an illustration of a screenshot is not.
 *
 * These are the MODULES and the daily operations — the portal, leases,
 * inspections, repairs, meters, money going out, messages, and getting your
 * data in and out. The dashboards and reports are on the home page instead,
 * where the question being asked is how the portfolio is performing.
 *
 * Captured from the demo account, so the figures are seeded rather than any
 * real landlord's. Sources live in PMS PROJECT DOCUMENTATION/screens; only
 * the chosen few were resized into web/public/screens as WebP — 1440px wide,
 * about 40KB each, because a page of full-size PNGs is megabytes nobody asked
 * for. Screens showing an empty state were left out: 'No documents yet' tells
 * a visitor nothing except that we had nothing to show them.
 */
/*
 * FIVE, AND WITHOUT THE MENU.
 *
 * Cropped to the content pane in the FILES, not hidden with CSS: the left
 * navigation spells out the shape of the whole product, and an asset anybody
 * can fetch directly is not hidden by styling.
 *
 * Nine became five. The four that went — leases, receipts, utilities and bulk
 * SMS — are real screens, but a visitor who has already seen five has stopped
 * counting, and the homepage makes the money case in full. What is left is the
 * part About is actually for: what a TENANT sees, the operational work a
 * landlord does between rent days, and the way a spreadsheet becomes an
 * account.
 */
const TOUR = [
  { src: '/screens/tenant-portal.webp?v=2', alt: 'The tenant portal rent screen showing a tenant their charges and balance',
    caption: 'What your tenant sees in their own portal: their rent, balance and history.' },
  { src: '/screens/inspection.webp?v=2', alt: 'An inspection showing room-by-room condition, photos and meter readings',
    caption: 'Inspections room by room, with photos, meter readings and a tenant signature.' },
  { src: '/screens/maintenance.webp?v=2', alt: 'The maintenance screen showing logged jobs with priority, age, cost and status',
    caption: 'Repairs logged, prioritised and costed, tracked through to done.' },
  { src: '/screens/expenses.webp?v=2', alt: 'The expenses screen showing total spend and a breakdown by category',
    caption: 'What the portfolio costs to run, booked against the property that spent it.' },
  { src: '/screens/import-export.webp?v=2', alt: 'The import and export screen showing the guided four-step import wizard',
    caption: 'Bring your spreadsheet in through a guided import, and take it all back out.' },
];

const ABOUT_SLIDES = [
  { src: '/cinematic/skyline.jpg', alt: 'A modern residential neighbourhood in Nairobi at golden hour', caption: 'Built for the buildings you look after.' },
  { src: '/about/slide-landlord.jpg', alt: 'A Kenyan landlord reviewing her property portfolio on a laptop in a bright home office', caption: 'Your whole portfolio, on one screen you can read at a glance.' },
  { src: '/about/slide-tenant.jpg', alt: 'A tenant paying rent from his phone on an apartment balcony in Nairobi', caption: 'Tenants pay from the phone already in their pocket.' },
  { src: '/about/slide-inspection.jpg', alt: 'A property manager and a caretaker walking through an empty apartment doing a move-in inspection', caption: 'Move-in and move-out inspections, room by room, with evidence.' },
  { src: '/about/slide-estate.jpg', alt: 'A gated townhouse estate in Nairobi with landscaped gardens and a family walking home', caption: 'From a single building to a growing estate.' },
  { src: '/about/slide-network.jpg', alt: 'An apartment building at blue hour with glowing teal lines joining the lit windows', caption: 'Every unit connected, and the intelligence to tell you what needs attention.' },
];

/* Clean line icons for the differentiator cards, in the site's own visual
   language (a glyph in a rounded brand-tinted square). */
const svg = (children: ReactNode) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const ICONS: Record<string, ReactNode> = {
  landlords: svg(<><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20h14V9.5" /><path d="M10 20v-5h4v5" /></>),
  simple: svg(<path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7z" />),
  scalable: svg(<><ellipse cx="12" cy="6.5" rx="7" ry="3" /><path d="M5 6.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" /><path d="M5 11.5v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" /></>),
  connected: svg(<><circle cx="6" cy="7" r="2.2" /><circle cx="18" cy="7" r="2.2" /><circle cx="12" cy="18" r="2.2" /><path d="M8 8l3 8M16 8l-3 8M8.2 7h7.6" /></>),
  africa: svg(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c3.2 2.6 3.2 15.4 0 18" /><path d="M12 3c-3.2 2.6-3.2 15.4 0 18" /></>),
  growth: svg(<><path d="M4 16l5-5 3.5 3.5L20 7" /><path d="M15 7h5v5" /></>),

  // Capability-catalogue icons. Same language as the cards above: one stroke
  // glyph per capability, drawn rather than emoji so they keep their weight in
  // dark mode and at the size the card renders them.
  ai: svg(<><path d="M11 3l1.6 4.4L17 9l-4.4 1.6L11 15l-1.6-4.4L5 9l4.4-1.6z" /><path d="M18 14l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></>),
  portal: svg(<><rect x="3.5" y="4" width="17" height="16" rx="2" /><path d="M3.5 8.5h17" /><circle cx="12" cy="13" r="1.9" /><path d="M8.7 18a3.4 3.4 0 0 1 6.6 0" /></>),
  lease: svg(<><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 17c1.2-2 2-.4 3-1.6s1.4-2.3 3-1.3" /></>),
  rent: svg(<><rect x="2.5" y="6" width="19" height="12" rx="2" /><circle cx="12" cy="12" r="2.6" /><path d="M6 10v4M18 10v4" /></>),
  invoice: svg(<><path d="M6 3h12v18l-3-1.6L12 21l-3-1.6L6 21z" /><path d="M9 8h6M9 12h5" /></>),
  send: svg(<><path d="M3 11.5 21 4l-7 17-2.6-7z" /><path d="M11.4 14.5 21 4" /></>),
  inspect: svg(<><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V2.9h6V4" /><path d="M8.6 11.6l2 2 4.4-4.4" /></>),
  listing: svg(<><circle cx="11" cy="11" r="7" /><path d="M16.2 16.2 21 21" /><path d="M8.2 11.4 11 9.1l2.8 2.3" /><path d="M9.2 10.6V14h3.6v-3.4" /></>),
  upkeep: svg(<><path d="M15.4 3.6a4.5 4.5 0 0 0-4.1 6L3.6 17.3a1.9 1.9 0 0 0 2.7 2.7l7.7-7.7a4.5 4.5 0 0 0 6-4.1l-2.9 2.1-2.8-.9-.9-2.8z" /></>),
  reports: svg(<><path d="M4 4v16h16" /><path d="M7.5 17v-4.5M12 17V9M16.5 17v-7" /></>),
  transfer: svg(<><path d="M12 3v10" /><path d="M8 9.4l4 4 4-4" /><path d="M4 16.5V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2.5" /></>),
  secure: svg(<><path d="M12 3l7.5 3v6c0 4.2-3 7.7-7.5 9-4.5-1.3-7.5-4.8-7.5-9V6z" /><path d="M9 12l2.2 2.2L15.4 10" /></>),
};

// Current product facts, phrased for the About page. Nothing here is a future
// promise — every capability listed exists today.
const DIFFERENTIATORS = [
  { icon: 'landlords', ic: 'blue', title: 'Built for landlords', shot: 'dashboard', shotLabel: 'Dashboard', body: 'Designed around the everyday needs of property owners and managers, without unnecessary enterprise complexity.' },
  { icon: 'simple', ic: 'violet', title: 'Simple by design', shot: 'rent-payments', shotLabel: 'Rent and payments', body: 'Powerful property management presented through an intuitive experience that does not require technical expertise.' },
  { icon: 'scalable', ic: 'teal', title: 'Affordable and scalable', shot: 'owner-portfolio', shotLabel: 'Portfolio view', body: 'Start with what you need today and upgrade as your property portfolio grows.' },
  { icon: 'connected', ic: 'green', title: 'Connected property management', shot: 'financials', shotLabel: 'Financials', body: 'Properties, units, tenants, leases, rent, invoices, inspections, maintenance and portfolio insight in one platform, not seven tools that do not speak to each other.' },
  { icon: 'africa', ic: 'amber', title: 'Designed for Africa, built to scale', shot: 'receipt', shotLabel: 'Receipts', body: 'Created with the realities of the African property market in mind, and built to support owners and managers beyond Kenya.' },
  { icon: 'growth', ic: 'terra', title: 'Built for growth', shot: 'vacancies', shotLabel: 'Vacancies', body: 'Helps landlords not only manage what they own, but make better decisions, manage vacancies and grow their portfolios.' },
  { icon: 'ai', ic: 'violet', title: 'An assistant that knows your portfolio', shot: 'ai-agents', shotLabel: 'AI agents', body: 'Ask who is in arrears or which leases expire soon and get the answer from your own data. It explains what it finds; it never changes a record on its own.' },
  { icon: 'portal', ic: 'blue', title: 'Your tenants are included', shot: 'tenant-portal', shotLabel: 'Tenant portal', body: 'Every tenant can have their own login to see their balance, statements and documents and to raise a repair. It is part of the product, not an extra to buy.' },
  { icon: 'transfer', ic: 'rose', title: 'No lock-in, ever', shot: 'import-export', shotLabel: 'Import and export', body: 'Bring your spreadsheet in with a guided import, and take everything back out as a spreadsheet whenever you want. Your records stay yours.' },
];

/*
 * The capability catalogue — what is actually in the platform today.
 *
 * This section exists because the About page had drifted badly from the
 * product. It described six qualities and a roadmap, and a visitor came away
 * believing the tenant portal, property listings, lease management and the
 * assistant were all still to come. Every one of them had shipped. A feature
 * nobody knows about is, commercially, a feature that does not exist.
 *
 * The rule for this array: an entry earns its place by being reachable in the
 * running product. Anything planned belongs in JOURNEY below, under Next or
 * Future, where a visitor can see it is a promise rather than a fact.
 */
const CAPABILITIES = [
  {
    icon: 'ai', ic: 'violet', group: 'Intelligence', name: 'NyumbaSuite AI', tag: 'An assistant for landlords and tenants',
    body: 'Ask a question in plain words, such as who is behind on rent, which units are empty or which leases expire soon, and get the answer from your own portfolio.',
    points: [
      'Answers built from your live data, never a canned reply',
      'Tenants get their own assistant inside their portal',
      'It reads and explains; it can never change your records',
    ],
  },
  {
    icon: 'portal', ic: 'blue', group: 'People', name: 'Tenant portal', tag: 'Self-service, by invitation',
    body: 'Give a tenant their own login and they stop ringing you for things they can look up themselves.',
    points: [
      'Balance, rent charges, invoices, payments and statements',
      'Their tenancy, lease and documents in one place',
      'Raise a maintenance request and follow it to done',
      'Notices land in the portal without spending an SMS',
    ],
  },
  {
    icon: 'lease', ic: 'teal', group: 'People', name: 'Lease agreements', tag: 'The document, not just the dates',
    body: 'A lease with a life of its own: drafted, activated, renewed or ended, with the history kept.',
    points: [
      'Draft to active to renewal, tracked at each step',
      'Expiry reminders at 90, 60 and 30 days',
      'Renewal history, so a long tenant reads as one story',
    ],
  },
  {
    icon: 'rent', ic: 'green', group: 'Money', name: 'Rent and payments', tag: 'The ledger underneath everything',
    body: 'Charges are raised each month for every tenancy, and what comes in is allocated against what is owed.',
    points: [
      'Full, part and advance payments handled correctly',
      'Receipts numbered in sequence, with no gaps',
      'Arrears banded by 7, 30, 60 and 90 days',
      'M-Pesa, bank, cash and cheque recorded with their reference',
    ],
  },
  {
    icon: 'invoice', ic: 'amber', group: 'Money', name: 'Invoices and statements', tag: 'Beyond the monthly rent',
    body: 'Itemised invoices for deposits, fees and one-off charges, and a statement covering any period you choose.',
    points: [
      'Line-by-line invoices, drafted before they are issued',
      'Status follows the money: open, part paid, paid, overdue',
      'Statements over any date range, as a proper document',
    ],
  },
  {
    icon: 'send', ic: 'terra', group: 'Money', name: 'Send documents to tenants', tag: 'Email and SMS',
    body: 'An invoice, receipt or statement goes out as a PDF from the screen you are already on, and the send is recorded.',
    points: [
      'One button, with the tenant’s own address already filled in',
      'Rent reminders and overdue notices on the channels you choose',
      'Bulk SMS to a chosen group, costed before you send it',
      'A delivery log that keeps the failures, not only the successes',
    ],
  },
  {
    icon: 'inspect', ic: 'rose', group: 'Property', name: 'Move-in and move-out inspections', tag: 'Evidence, before the argument',
    body: 'Walk the unit room by room, record what you find, and have the tenant sign it from their own phone.',
    points: [
      'Room-by-room condition, with photos and meter readings',
      'The tenant signs by link, with no printing and no second visit',
      'Move-out compared against move-in, side by side',
      'A deposit worksheet built from the differences',
    ],
  },
  {
    icon: 'listing', ic: 'blue', group: 'Property', name: 'Listings and home search', tag: 'Fill the vacancy',
    body: 'Advertise a vacant unit straight from your portfolio, and let people looking for a home find it.',
    points: [
      'Publish from the unit you already have, with no re-typing',
      'Searchable by area, rent, type and bedrooms, with photos',
      'Enquiries come back to you inside NyumbaSuite',
    ],
  },
  {
    icon: 'upkeep', ic: 'amber', group: 'Property', name: 'Maintenance, utilities and expenses', tag: 'Running the building',
    body: 'The work, the meters and the money that keep a property standing, recorded against the property they belong to.',
    points: [
      'Maintenance requests tracked through to resolution',
      'Water and electricity readings per unit, period by period',
      'Expenses booked to the right property, with documents attached',
    ],
  },
  {
    icon: 'reports', ic: 'violet', group: 'Intelligence', name: 'Reports and analytics', tag: 'Twenty-one reports',
    body: 'Occupancy, collection, arrears, income against expense and profitability per property, presented as answers rather than raw charts.',
    points: [
      'Rent roll, arrears aging, income statement, profitability',
      'A Monthly Rental Income summary shaped for KRA filing',
      'Every report downloads as a spreadsheet or a PDF',
    ],
  },
  {
    icon: 'transfer', ic: 'teal', group: 'Data', name: 'Bring your data with you', tag: 'Import and export',
    body: 'Move properties, units, tenants, tenancies and payment history in from the spreadsheet you keep today.',
    points: [
      'A template per record type, so the columns are never a guess',
      'A dry run that reports every problem before anything is written',
      'Export the same data back out whenever you want it',
    ],
  },
  {
    icon: 'secure', ic: 'green', group: 'Data', name: 'Your team, and your data', tag: 'Roles and isolation',
    body: 'Give a caretaker, manager or accountant a login limited to what they need, and keep your portfolio yours.',
    points: [
      'Separate roles for managers, finance staff and caretakers',
      'Separation enforced by the database, not by application code',
      'An audit trail of who did what, and when',
    ],
  },
];

// The three-stage direction. `tone` drives the badge colour so a visitor can
// tell at a glance what is available today from what is planned or exploratory.
//
// "Now" was badly understated and "Next" badly overstated: the tenant portal,
// property listings, utilities and the assistant all sat under future work
// months after they shipped. It is kept honest in both directions now — nothing
// in Now that a visitor cannot open today, nothing in Next that is already done.
const JOURNEY = [
  { tone: 'now', stage: 'Now', badge: 'Available today', title: 'Run the whole portfolio',
    items: ['Properties and units', 'Tenants and tenancies', 'Leases', 'Rent, payments and receipts', 'Invoices and statements', 'Tenant portal', 'Inspections', 'Maintenance, utilities and expenses', 'Listings and home search', 'SMS, email and in-app notices', 'AI assistant', 'Twenty-one reports'] },
  { tone: 'next', stage: 'Next', badge: 'On the roadmap', title: 'Close the loop on money and messages',
    items: ['Collect rent by M-Pesa directly', 'Statements on a schedule you set', 'WhatsApp as a delivery channel', 'A marketplace of trusted service providers', 'Recurring maintenance faults detected automatically', 'A daily briefing of what needs you first', 'Deeper automation of the monthly cycle'] },
  { tone: 'future', stage: 'Future', badge: 'Exploring', title: 'Property intelligence',
    items: ['Collection risk flagged before arrears build', 'Rent guidance from real market data', 'Cash-flow and occupancy forecasting', 'An advisor that understands the whole process, not just the question', 'Your portfolio readable from the AI tools you already use'] },
];

// The accent cycles the icon palette so the four values read as four distinct
// cards at a glance rather than one grey wall. Colour here is decorative, which
// is why it is only used where nothing depends on it being read.
const VALUES = [
  {
    accent: 'blue',
    title: 'Built for the landlord, not the enterprise',
    body: 'Most property software is priced and shaped for large agencies. We build for the landlord with a handful of units who deserves the same rigour without the same overhead.',
  },
  {
    accent: 'green',
    title: 'The numbers must always agree',
    body: 'Rent, payments, allocation and arrears are handled with the care of an accounting ledger. If a figure is wrong, trust is gone, so the arithmetic is never guessed.',
  },
  {
    accent: 'violet',
    title: 'Understandable on first open',
    body: 'A landlord should not need a training day. Every screen is measured against one test: can someone manage their property here without being taught how?',
  },
  {
    accent: 'amber',
    title: 'Priced for East Africa',
    body: 'Pricing is set for the market it serves, in the currency it serves, so the tool pays for itself on a single recovered late payment.',
  },
];

/*
 * The collections agent, drawn as the process it runs, not a claim about AI.
 *
 * This is the "COLLECTION RISK" lifecycle from the intelligence brief: a missed
 * payment turned into a resolved one, step by step. It is on the About page to
 * SHOW the intelligence rather than assert it, and it is honest about its own
 * seam — `live` marks the steps that run today (arrears banded by age, the bulk
 * reminder that drafts a message per tenant, waits for a person to approve it,
 * sends it and logs every delivery), and the rest are the insight spine being
 * built (reading repeat lateness and rising balances together, ranking who to
 * chase, and bringing an unpaid account back around on its own). Marking the
 * three unbuilt steps is the same rule the "Coming soon" tags follow elsewhere:
 * the power of the picture depends on the live steps being true.
 */
const COLLECTIONS_FLOW: { n: number; label: string; body: string; live: boolean; detail: string }[] = [
  { n: 1, label: 'Detect', body: 'A tenant slips behind, banded by how overdue.', live: true,
    detail: 'Every unpaid charge is placed in an age band from the day its grace period ends, so you see who is a few days late and who is months behind.' },
  { n: 2, label: 'Analyse', body: 'Repeat lateness and a rising balance, read together.', live: true,
    detail: 'The collections agent looks beyond today: it rebuilds what each tenant owed at the end of each month, and counts how often they pay after the due date, so a balance that keeps growing is caught early.' },
  { n: 3, label: 'Recommend', body: 'Who to chase first, who to handle in person.', live: true,
    detail: 'The Arrears page ranks tenants for the first call on how long, how much, and what the agent has noticed, and says why in words, so the order is never a mystery.' },
  { n: 4, label: 'Draft', body: 'A message per tenant, carrying their own balance.', live: true,
    detail: 'Each tenant gets their own message with their own balance in it, not one shared template with a blank to fill.' },
  { n: 5, label: 'Approve', body: 'You see the list and the wording before it moves.', live: true,
    detail: 'Nothing is sent on its own. You see who will get a message and exactly what it says, and you decide.' },
  { n: 6, label: 'Send', body: 'Out on the channel you chose, by SMS or email.', live: true,
    detail: 'Messages go out on the channel you picked, and a tenant reminded recently is skipped so nobody is texted twice in a day.' },
  { n: 7, label: 'Monitor', body: 'Every delivery logged, the failures kept too.', live: true,
    detail: 'Each message is logged as delivered or failed. The failures stay on the record, so you know who never received it.' },
  { n: 8, label: 'Escalate', body: 'Still unpaid, it comes back around on its own.', live: false,
    detail: 'The next step in the loop: an account still unpaid after the date you set returns to the top of the work order without anyone having to remember. This one is being built.' },
];

/*
 * The specification table.
 *
 * A buyer comparing three products asks the same eight questions in the first
 * two minutes — who is it for, does it handle MY kind of building, can it take
 * M-Pesa, does it work on a phone, what does it cost — and until now every
 * answer was somewhere else on the site, inferred rather than stated. It is
 * also the shape a search engine, a comparison article and an AI assistant
 * can lift verbatim, which prose is not.
 *
 * The property types are the PROPERTY_TYPES enum from src/domain/portfolio.ts
 * and the payment methods are the payment_method enum, both written out in
 * plain words. If either enum changes, this list is wrong — which is the
 * right kind of coupling: a claim about the product that breaks visibly when
 * the product moves.
 *
 * Pricing is described by its SHAPE and not by its numbers. Rates, bands and
 * the annual discount are live configuration read from /api/v1/pricing, and a
 * figure typed in here would be the one place on the site that quietly
 * disagrees with the billing engine.
 */
const PROPERTY_TYPES_LABELS = [
  'Apartments', 'Residential houses', 'Bedsitter complexes', 'Student accommodation',
  'Commercial buildings', 'Offices', 'Shops', 'Warehouses', 'Mixed-use developments',
];

const FACTS: { label: string; value: ReactNode }[] = [
  { label: 'What it is', value: 'Property management software for landlords: properties, tenants, rent, payments, leases, invoices, inspections, maintenance and reporting in one platform.' },
  { label: 'Built for', value: 'Individual landlords, growing portfolios and the property managers, caretakers, finance staff and accountants who work with them.' },
  { label: 'Where', value: 'Kenya, built to serve East Africa. Prices in Kenyan shillings.' },
  {
    label: 'Property types',
    value: (
      <span className="mk-fact-chips">
        {PROPERTY_TYPES_LABELS.map((t) => <span key={t} className="mk-fact-chip">{t}</span>)}
      </span>
    ),
  },
  { label: 'Payments recorded', value: 'M-Pesa, bank transfer, cash and cheque, each kept with its own reference and allocated against what is owed. Collecting rent by M-Pesa directly is on the roadmap; today a payment is recorded, not pulled.' },
  { label: 'Reaching tenants', value: 'SMS, email and notices inside the tenant portal, with rules you set per event. WhatsApp is planned.' },
  { label: 'Tenant access', value: 'Every tenant can be invited to a portal of their own for balances, statements, documents and repairs. There is no public tenant sign-up. Access comes from you.' },
  { label: 'Reporting', value: 'Twenty-one reports including a Monthly Rental Income summary shaped for KRA filing. Every one downloads as a spreadsheet or a PDF.' },
  { label: 'Runs on', value: 'Any browser, and it installs to a phone home screen like an app. Nothing to download from a store.' },
  { label: 'Pricing', value: <>Progressive per-unit pricing in Kenyan shillings, paid by M-Pesa. The rate falls as the portfolio grows, with a discount for annual billing. <Link to="/#pricing">See the live rates</Link>.</> },
  { label: 'Your data', value: 'Bring a spreadsheet in through a guided import that dry-runs every row first, and export everything back out whenever you want. No lock-in.' },
];

/** The About page: who we are, why NyumbaSuite exists, and what we stand for. */
export function About() {
  /*
   * React Router does not scroll to a #hash of its own accord, so a link from
   * another page to /about#platform changed the URL and left the visitor at
   * the top of the page, apparently ignored. Same handling as the Solutions
   * page, deferred a touch so the hero image has laid out and the target
   * lands where it should.
   */
  /* Was its own copy of the same 60ms guess — see useHashScroll. */
  useHashScroll();
  useReveal();

  return (
    <div className="lp mk">
      <PublicHeader />

      <section className="mk-hero">
        <span className="mk-eyebrow">About NyumbaSuite</span>
        <h1 className="mk-title">Built for Africa. Designed to think ahead.</h1>
        <p className="mk-lede">
          NyumbaSuite is property management software for small and growing landlords across
          Kenya and East Africa, built around how rent is actually collected here, and
          designed to respect the landlord rather than the enterprise. Rent, tenants, leases,
          invoices, inspections, maintenance and a portal of their own for every tenant, with
          the intelligence to tell you what needs attention before it costs you.
        </p>
      </section>

      <section className="mk-section" style={{ paddingTop: 0 }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <CinematicReel slides={ABOUT_SLIDES} />
        </div>
      </section>

      <section className="mk-section">
        <div className="lp-split">
          <div>
            <h2 className="mk-h2">Our story</h2>
            <p className="mk-p">
              Landlords across the region still run their portfolios on paper, spreadsheets
              and memory. Rent gets missed, receipts get lost, and the true state of a
              property is never more than a guess. The tools that could help were built for
              large agencies in other markets, priced and shaped for someone else.
            </p>
            <p className="mk-p">
              NyumbaSuite started from a simple conviction: a landlord with ten units
              deserves the same clarity a large firm takes for granted, at a price that
              makes sense here, on the phone already in their pocket.
            </p>
          </div>
          <img src="/estate-townhouses.webp" width={1200} height={896}
            alt="A row of modern townhouses in a gated estate" />
        </div>
      </section>

      <div className="mk-band-wrap">
        <CinematicBand src="/cinematic/landlord.jpg" alt="A landlord checking his portfolio from his balcony"
          kicker="Our idea" line="A landlord with ten units deserves a clear view of them." />
      </div>

      <section className="mk-section">
        <div className="mk-center">
          <span className="mk-eyebrow">What we stand for</span>
          <h2 className="mk-h2">Why NyumbaSuite is built the way it is</h2>
        </div>
        <ValuesAccordion values={VALUES}
          vision="A region where every landlord, however small, runs their property with confidence and complete visibility, and every tenant is dealt with fairly and on record."
          mission="To give landlords across East Africa property management software that is accurate, affordable and understandable, and to keep the core landlord workflow excellent before adding anything else." />
      </section>

      {/* ---- how the intelligence works --------------------------------
          The differentiator cards name the AI assistant; this shows what the
          product's intelligence actually DOES with a problem, end to end,
          rather than repeating the word. Half of it runs today and half is
          the insight spine being built, and the two are told apart on the
          face of the diagram. See COLLECTIONS_FLOW for the seam. */}
      <section className="mk-section tint mk-intel">
        <div className="mk-center">
          <span className="mk-eyebrow">Intelligence at work</span>
          <h2 className="mk-h2">From a missed payment to a resolved one</h2>
          <p className="mk-lede">
            NyumbaSuite does not just tell you a tenant is behind. It works the problem the way
            you would, one step at a time, and asks before it acts on your behalf.
          </p>
        </div>
        <WorkflowExplorer steps={COLLECTIONS_FLOW}
          imageSrc="/about/workflow.png" videoSrc="/about/loop.mp4" posterSrc="/about/slide-network.jpg" />
      </section>

      <section className="mk-section">
        <div className="mk-center">
          <span className="mk-eyebrow">What makes us different</span>
          <h2 className="mk-h2">More than property management. A smarter way to manage your portfolio.</h2>
          <p className="mk-lede">
            NyumbaSuite is designed around the real needs of today's landlords. Instead of
            spreadsheets, notebooks, scattered messages and disconnected tools, it brings
            property, tenant, rent and portfolio information together in one simple platform.
            The full list of what that includes is <a href="#platform">further down this page</a>.
          </p>
        </div>
        <PillarStage items={DIFFERENTIATORS} icons={ICONS} />
      </section>

      {/* ---- what is actually in the box -------------------------------
          The differentiator cards above say what kind of product this is.
          They do not say what it does, and for a long time nothing on the
          public site did — a visitor could read every marketing page and
          never learn that tenants get their own portal or that leases,
          inspections and listings exist at all. This section is the answer
          to "what do I actually get", and it is a catalogue, not a pitch. */}
      <div className="mk-band-wrap">
        <CinematicBand src="/cinematic/owner.jpg" alt="An owner and her accountant reviewing statements"
          kicker="What is inside" line="Everything below is in the product today." chips={['Rent', 'Leases', 'Inspections', 'Maintenance', 'Portal']} />
      </div>

      <section className="mk-section" id="platform">
        <div className="mk-center">
          <span className="mk-eyebrow">What's inside</span>
          <h2 className="mk-h2">Everything in the platform today</h2>
          <p className="mk-lede">
            Not a roadmap. Every capability below is in NyumbaSuite right now, and you
            can see it working from your phone on the day you sign up.
          </p>
        </div>
        <CapabilityExplorer items={CAPABILITIES} icons={ICONS}
          groups={['Money', 'People', 'Property', 'Intelligence', 'Data']} />
        <div className="mk-center" style={{ marginTop: 'var(--space-6)' }}>
          <Link className="btn btn-secondary" to="/solutions">See how each one works</Link>
        </div>
      </section>

      {/* ---- what it looks like -----------------------------------------
          Twenty-one capabilities described in words and not one picture of
          any of them. A visitor deciding whether software fits their week
          wants to see a screen, and the page asked them to imagine every one.

          These are drawn in markup rather than captured from the running app,
          on purpose. A screenshot of the demo carries whatever the demo
          happened to hold that afternoon, goes stale the first time a heading
          moves, and cannot be read by a screen reader. Built from the same
          tokens as the product, they follow the theme and stay legible when
          the text is enlarged. They are labelled as examples, because that is
          what they are. */}
      <section className="mk-section">
        <div className="mk-center">
          <span className="mk-eyebrow">What it looks like</span>
          <h2 className="mk-h2">Ten screens you would actually use</h2>
          <p className="mk-lede">
            Real screens from the product, moving on their own, or press a bar to stop
            on one. Demo data: property names, tenant names and phone numbers are hidden.
          </p>
        </div>
        <CinematicReel slides={TOUR} shape="screen" ms={4500} />
      </section>

      {/* ---- the specification, in one table --------------------------- */}
      <section className="mk-section narrow" id="at-a-glance">
        <div className="mk-center">
          <span className="mk-eyebrow">At a glance</span>
          <h2 className="mk-h2">NyumbaSuite, in one table</h2>
          <p className="mk-lede">
            The questions people ask before they will try anything, answered without
            reading the rest of the site.
          </p>
        </div>
        <GlanceFinder facts={FACTS} />
      </section>

      <section className="mk-section">
        <div className="mk-center">
          <span className="mk-eyebrow">Where we're going</span>
          <h2 className="mk-h2">Building the future of property management</h2>
          <p className="mk-lede">
            NyumbaSuite starts with a simple mission: make property management easier and
            more accessible for landlords. Our ambition goes further, toward a connected
            property ecosystem where landlords manage portfolios, collect and track rent,
            communicate with tenants, handle maintenance, understand performance, and connect
            vacant properties with people looking for homes, all from one platform.
          </p>
        </div>

        <RoadmapTrack stages={JOURNEY} />

        <div className="mk-vision">
          <p className="mk-vision-lead">
            To become a leading property technology platform connecting property owners,
            managers and tenants across Africa.
          </p>
          <p>
            We want NyumbaSuite to evolve from a property management platform into a complete
            digital ecosystem for managing, discovering and growing property portfolios.
          </p>
        </div>
      </section>

      <section className="mk-cta">
        <h2>Ready to see NyumbaSuite?</h2>
        <p>Create your account, or ask for a walkthrough built around your own portfolio.</p>
        <div className="mk-cta-row">
          <Link className="btn btn-invert" to="/register">Get started</Link>
          <Link className="btn btn-outline-invert" to="/demo">Request a demo</Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
